import { messageError } from '../../components/message';
import { withAPIAccount } from '../../lib/http/api';
import { APIError, parsedResponse } from '../../lib/http/http';
import { AccountType, IAPIresponse } from '../../model';
import {
  IAPIServiceConfigItem,
  IAPIServicesApproveItem,
  IServiceStatus,
  IServiceApplyCreateDto,
  IServiceAllEnvs,
  EnableType,
  IServiceApplyInfo,
  IClientAPI,
  IGauthServiceType,
  IGauthApplyBody,
  IGauthModifyBody,
  IServerVersion,
} from '../../model/api-modules';
import {
  SERVICE_PREFIX,
  INGAME_CONSOLE_PROXY_PREFIX,
  INGAME_CONSOLE_PROXY_PREFIX_GAME,
  GAUTH_PROXY_PREFIX,
  API_PREFIX,
} from '../config';

// 服务配置-列表
export const getServiceList = () =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${SERVICE_PREFIX}/config`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<IAPIServiceConfigItem[]>(
        response,
        'data'
      );
      return res;
    } catch (error) {
      return null;
    }
  });

// 服务开通-申请
export const applyService = (body: IServiceApplyCreateDto) =>
  withAPIAccount(AccountType.auto, async (api): Promise<IAPIresponse> => {
    try {
      const url = `${SERVICE_PREFIX}/apply`;
      const response = await api.commonRequest('POST', url, body);
      const res = await parsedResponse<IAPIresponse>(response, 'all');
      return res;
    } catch (error) {
      throw error;
    }
  });

// 服务开通-申请
export const batchApplyService = (
  toLink: string,
  gameId: string,
  envID: Number,
  serviceID: ReadonlyArray<number>
) =>
  withAPIAccount(AccountType.auto, async (api): Promise<IAPIresponse> => {
    try {
      const url = `${SERVICE_PREFIX}/apply/${gameId}/${envID}`;
      const response = await api.commonRequest('POST', url, {
        toLink: encodeURIComponent(toLink),
        serviceID,
      });
      const res = await parsedResponse<IAPIresponse>(response, 'all');
      return res;
    } catch (error) {
      throw error;
    }
  });

// 服务开通-列表-<包括已下线服务>,Status: 已开通:1, 拒绝：2
export const serviceOpenList = (gameID: string, envID: Number) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${SERVICE_PREFIX}/opened-status?gameID=${gameID}&envID=${envID}`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<IAPIServicesApproveItem[]>(
        response,
        'data'
      );
      return res;
    } catch (error) {
      return [];
    }
  });

// 获取服务开通的审批状态
export const getServiceStatus = (
  gameID: string,
  serviceID: Number,
  envID: Number
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${SERVICE_PREFIX}/status/${gameID}/${envID}/${serviceID}`;
      const response = await api.commonRequest('GET', url, envID);
      const res = await parsedResponse<IServiceStatus[]>(response, 'data');
      return res;
    } catch (error: any) {
      messageError(error?.message);
      return null;
    }
  });

// 获取所有服务开通的审批状态
export const getAllServiceStatus = (gameID: string, envID: Number) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${SERVICE_PREFIX}/status/${gameID}/${envID}`;
      const response = await api.commonRequest('GET', url, envID);
      const res = await parsedResponse<Record<string, IServiceStatus[] | null>>(
        response,
        'data'
      );
      return res;
    } catch (error: any) {
      messageError(error?.message);
      return null;
    }
  });

// 获取服务所有环境下的开通状态
export const getAllEnvsServiceStatus = (gameID: string, serviceID: string) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${SERVICE_PREFIX}/status-envs/${gameID}/${serviceID}`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<
        Record<string, IServiceAllEnvs[] | null>
      >(response, 'data');
      return res;
    } catch (error: any) {
      messageError(error?.message);
      return null;
    }
  });

// 开服
export const openService = (
  gameId: string,
  envId: number,
  envGuid: string,
  enableType: EnableType,
  expectedExecutionTime?: number
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${INGAME_CONSOLE_PROXY_PREFIX}/v1/api/k8s/ingame/game_service_env/enable_service`;
      const body: any = {
        gameID: gameId,
        envID: envId,
        game_id: gameId,
        env_id: envGuid,
        enable_type: enableType,
      };
      if (expectedExecutionTime) {
        body.expected_execution_time = expectedExecutionTime;
      }
      const response = await api.commonRequest('POST', url, body);
      const res = await parsedResponse<any>(response, 'code');
      return res;
    } catch (error: any) {
      messageError(error?.message);
      return null;
    }
  });

// 切换开服类型
export const changeServiceType = (
  gameId: string,
  envId: number,
  envGuid: string,
  enableType: EnableType,
  expectedExecutionTime?: number
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${INGAME_CONSOLE_PROXY_PREFIX}/v1/api/k8s/ingame/game_service_env/change_service_open_type`;
      const body: any = {
        gameID: gameId,
        envID: envId,
        game_id: gameId,
        env_id: envGuid,
        enable_type: enableType,
      };
      if (expectedExecutionTime) {
        body.expected_execution_time = expectedExecutionTime;
      }
      const response = await api.commonRequest('POST', url, body);
      const res = await parsedResponse<any>(response, 'code');
      return res;
    } catch (error: any) {
      messageError(error?.message);
      return null;
    }
  });

// 关服
export const closeService = (
  gameId: string,
  envId: number,
  envGuid: string,
  expectedExecutionTime?: number
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${INGAME_CONSOLE_PROXY_PREFIX}/v1/api/k8s/ingame/game_service_env/disable_service`;
      const body: any = {
        gameID: gameId,
        envID: envId,
        game_id: gameId,
        env_id: envGuid,
      };
      if (expectedExecutionTime) {
        body.expected_execution_time = expectedExecutionTime;
      }
      const response = await api.commonRequest('POST', url, body);
      const res = await parsedResponse<any>(response, 'code');
      return res;
    } catch (error: any) {
      messageError(error?.message);
      return null;
    }
  });

// 取消任务
export const cancelTask = (
  gameId: string,
  envId: number,
  envGuid: string,
  taskId: number
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${INGAME_CONSOLE_PROXY_PREFIX}/v1/api/k8s/ingame/game_service_env/cancel_task`;
      const response = await api.commonRequest('POST', url, {
        gameID: gameId,
        envID: envId,
        game_id: gameId,
        env_id: envGuid,
        task_id: taskId,
      });
      const res = await parsedResponse<any>(response, 'code');
      return res;
    } catch (error: any) {
      messageError(error?.message);
      return error?.apiError?.code || null;
    }
  });

// 查询当前游戏下env的停开服状态和任务信息
export const getServiceStatusByEnv = (
  gameId: string,
  envId: number,
  envGuid: string
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${INGAME_CONSOLE_PROXY_PREFIX_GAME}/v1/api/k8s/ingame/game_service_env/get_service_status?gameID=${gameId}&envID=${envId}&game_id=${gameId}&env_id=${envGuid}`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<any>(response, 'data');
      return res;
    } catch (error: any) {
      // messageError(error?.message);
      return null;
    }
  });

// 查询当前游戏下所有服务的申请信息填写情况
export const getServiceApplyInfo = (gameId: string) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${SERVICE_PREFIX}/apply-info/${gameId}`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<IServiceApplyInfo[]>(response, 'data');
      return res;
    } catch (error: any) {
      // messageError(error?.message);
      return null;
    }
  });

// 查询当前选择的游戏和环境下ClientAPI以及草稿状态
export const getClientAPI = (gameId: string, envId: number, envGuid: string) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${INGAME_CONSOLE_PROXY_PREFIX}/v1/api/k8s/ingame/game_service_client_api/query_client_api_draft?gameID=${gameId}&envID=${envId}&game_id=${gameId}&env_id=${envGuid}`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<IClientAPI>(response, 'data');
      return res;
    } catch (error: any) {
      // messageError(error?.message);
      return null;
    }
  });

// 查询历史任务
export const queryHistoryTask = (
  gameId: string,
  envId: number,
  envGuid: string,
  taskType: number
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${INGAME_CONSOLE_PROXY_PREFIX}/v1/api/k8s/k8s_app/tasks?gameID=${gameId}&envID=${envId}&game_id=${gameId}&env_id=${envGuid}&k8s_app_task_type=${taskType}&curr_page=1&page_count=1`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<any>(response, 'data');
      return res;
    } catch (error: any) {
      // messageError(error?.message);
      return null;
    }
  });

// 单个Client接口开关
export const modifyClientApiDraft = (
  gameId: string,
  envId: number,
  envGuid: string,
  service: string,
  client_api_key: string,
  is_enabled: boolean,
  module_name: string,
  package_name_service_name: string
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${INGAME_CONSOLE_PROXY_PREFIX}/v1/api/k8s/ingame/game_service_client_api/modify_client_api_draft`;
      const response = await api.commonRequest('POST', url, {
        gameID: gameId,
        envID: envId,
        game_id: gameId,
        env_id: envGuid,
        service,
        client_api_key,
        is_enabled,
        module_name,
        package_name_service_name,
      });
      const res = await parsedResponse<any>(response, 'code');
      return res;
    } catch (error: any) {
      messageError(error?.message);
      return error?.apiError?.code || null;
    }
  });

// 放弃草稿态
export const discardClientApiDraft = (
  gameId: string,
  envId: number,
  envGuid: string
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${INGAME_CONSOLE_PROXY_PREFIX}/v1/api/k8s/ingame/game_service_client_api/discard_client_api_draft`;
      const response = await api.commonRequest('POST', url, {
        gameID: gameId,
        envID: envId,
        game_id: gameId,
        env_id: envGuid,
      });
      const res = await parsedResponse<any>(response, 'code');
      return res;
    } catch (error: any) {
      messageError(error?.message);
      return error?.apiError?.code || null;
    }
  });

// 触发任务执行发布
export const publishClientApiDraft = (
  gameId: string,
  envId: number,
  envGuid: string,
  version: string,
  description: string
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${INGAME_CONSOLE_PROXY_PREFIX}/v1/api/k8s/ingame/game_service_client_api/publish_client_api_draft`;
      const response = await api.commonRequest('POST', url, {
        gameID: gameId,
        envID: envId,
        game_id: gameId,
        env_id: envGuid,
        version,
        description,
      });
      const res = await parsedResponse<any>(response, 'code');
      return res;
    } catch (error: any) {
      messageError(error?.message);
      return error?.apiError?.code || null;
    }
  });

// 获取GAuth业务编码-仅store服务
export const queryGauthCode = (gameID: string, serviceID: string) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${API_PREFIX}/service/gauth-service-type/${gameID}/${serviceID}`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<IGauthServiceType>(response, 'data');
      return res;
    } catch (error: any) {
      // messageError(error?.message);
      return null;
    }
  });

// gauth鉴权
export const queryGauthInfo = (
  platID: number,
  serviceType: string,
  gameID: string
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${GAUTH_PROXY_PREFIX}/getAuthInfo.php?action=getAuthList2&type=json&platID=${platID}&serviceType=${serviceType}&gameID=${gameID}`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<any>(response, 'all', false, true);
      return res;
    } catch (error: any) {
      console.log('failed:', error);
      if (error instanceof APIError) {
        return error.apiError;
      }
      return null;
    }
  });

// gauth权限申请 - 新用户
export const gauthApply = (
  platID: number,
  gameID: string,
  body: IGauthApplyBody
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${GAUTH_PROXY_PREFIX}/auth.php?action=ApplyAuthV2&type=json&platID=${platID}&gameID=${gameID}`;
      const response = await api.commonRequest('POST', url, body);
      const res = await parsedResponse<any>(response, 'all', false, true);
      return res;
    } catch (error: any) {
      console.log('failed:', error);
      if (error instanceof APIError) {
        return error.apiError;
      }
      return null;
    }
  });

// gauth权限续期 - 老用户
export const gauthModify = (
  platID: number,
  gameID: string,
  body: IGauthModifyBody
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${GAUTH_PROXY_PREFIX}/auth.php?action=ModifyAuthV2&type=json&platID=${platID}&gameID=${gameID}`;
      const response = await api.commonRequest('POST', url, body);
      const res = await parsedResponse<any>(response, 'all', false, true);
      return res;
    } catch (error: any) {
      console.log('failed:', error);
      if (error instanceof APIError) {
        return error.apiError;
      }
      return null;
    }
  });

// 查询服务版本
export const getServerVersion = (
  gameId: string,
  envId: number,
  envGuid: string
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${INGAME_CONSOLE_PROXY_PREFIX}/v1/api/basic/console_deploy_config/env_version_confirm?gameID=${gameId}&envID=${envId}&game_id=${gameId}&env_id=${envGuid}`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<IServerVersion>(response, 'all');
      return res;
    } catch (error: any) {
      // messageError(error?.message);
      return null;
    }
  });
